import React from 'react';
import { Navbar, Container, Button } from 'react-bootstrap';
import { Config } from '../config';
import useCompany from '../hooks/useCompany';
import '../styles/header.css';

interface HeaderProps {
    hideOnEstimatePage?: boolean;
    connectedAccountId?: string | null;
    companyId?: string | null;
}

const Header = ({ hideOnEstimatePage = false, connectedAccountId = null, companyId = null }: HeaderProps) => {
    const { company, loading } = useCompany(connectedAccountId, companyId);

    // Use company phone if available, otherwise use default
    const phoneNumberRaw = company?.phone?.replace(/\D/g, '') || "";
    const phoneNumberLink = `tel:${phoneNumberRaw}`;
    const phoneNumber = company?.phone || "";

    // If we're on the estimate page and hideOnEstimatePage is true, don't render the header
    if (hideOnEstimatePage && window.location.pathname.includes('/estimate/')) {
        return null;
    }

    // Get the current origin to ensure logo works correctly
    const origin = window.location.origin;

    return (
        <Navbar expand="lg" className="modern-header w-100">
            <Container>
                <Navbar.Brand href={company?.website ? `https://${company.website}` : ""} className="brand-section">
                    <div className="logo-wrapper">
                        <div className="logo-icon">
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="20" cy="20" r="20" fill="url(#gradient1)" />
                                <path d="M20 10L15 25H25L20 10Z" fill="white" />
                                <defs>
                                    <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#005fa9" />
                                        <stop offset="100%" stopColor="#0084ff" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        <div className="brand-text">
                            <span className="company-name">{loading ? "Loading..." : (company?.name || "")}</span>
                            <span className="company-tagline">Professional • Reliable • Trusted</span>
                        </div>
                    </div>
                </Navbar.Brand>
                <div className="d-flex align-items-center header-actions">
                    <div className="contact-info d-none d-md-flex flex-column me-4">
                        <a href={`mailto:${company?.email || ""}`} className="contact-item text-decoration-none">
                            <svg className="contact-icon" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M2.678 11.894a1 1 0 0 1 .287.801 10.97 10.97 0 0 1-.398 2c1.395-.323 2.247-.697 2.634-.893a1 1 0 0 1 .71-.074A8.06 8.06 0 0 0 8 14c3.996 0 7-2.807 7-6 0-3.192-3.004-6-7-6S1 4.808 1 8c0 1.468.617 2.83 1.678 3.894zm-.493 3.905a21.682 21.682 0 0 1-.713.129c-.2.032-.352-.176-.273-.362a9.68 9.68 0 0 0 .244-.637l.003-.01c.248-.72.45-1.548.524-2.319C.743 11.37 0 9.76 0 8c0-3.866 3.582-7 8-7s8 3.134 8 7-3.582 7-8 7a9.06 9.06 0 0 1-2.347-.306c-.52.263-1.639.742-3.468 1.105z" />
                            </svg>
                            <span>{company?.email || ""}</span>
                        </a>
                        <a href={company?.website ? `https://${company.website}` : ""} target="_blank" rel="noopener noreferrer" className="contact-item text-decoration-none">
                            <svg className="contact-icon" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm7.5-6.923c-.67.204-1.335.82-1.887 1.855A7.97 7.97 0 0 0 5.145 4H7.5V1.077zM4.09 4a9.267 9.267 0 0 1 .64-1.539 6.7 6.7 0 0 1 .597-.933A7.025 7.025 0 0 0 2.255 4H4.09zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a6.958 6.958 0 0 0-.656 2.5h2.49zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5H4.847zM8.5 5v2.5h2.99a12.495 12.495 0 0 0-.337-2.5H8.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5H4.51zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5H8.5zM5.145 12c.138.386.295.744.468 1.068.552 1.035 1.218 1.65 1.887 1.855V12H5.145zm.182 2.472a6.696 6.696 0 0 1-.597-.933A9.268 9.268 0 0 1 4.09 12H2.255a7.024 7.024 0 0 0 3.072 2.472zM3.82 11a13.652 13.652 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5H3.82zm6.853 3.472A7.024 7.024 0 0 0 13.745 12H11.91a9.27 9.27 0 0 1-.64 1.539 6.688 6.688 0 0 1-.597.933zM8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855.173-.324.33-.682.468-1.068H8.5zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.65 13.65 0 0 1-.312 2.5zm2.802-3.5a6.959 6.959 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5h2.49zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7.024 7.024 0 0 0-3.072-2.472c.218.284.418.598.597.933zM10.855 4a7.966 7.966 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4h2.355z" />
                            </svg>
                            <span>{company?.website || ""}</span>
                        </a>
                    </div>
                    <Button className="call-button" href={phoneNumberLink}>
                        <svg className="phone-icon" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z" />
                        </svg>
                        <span className="call-text">
                            <span className="call-label">Call Now</span>
                            <span className="call-number">{phoneNumber}</span>
                        </span>
                    </Button>
                </div>
            </Container>
        </Navbar>
    );
};

export default Header;
